import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Badge,
  Button,
  Spinner,
  LinkBox,
  LinkOverlay,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  FiSearch,
  FiList,
  FiUsers,
  FiGrid,
  FiMail,
  FiArrowRight,
  FiUserPlus,
} from 'react-icons/fi'
import { useAuth } from '../hooks/useAuth'
import { useCampaigns } from '../hooks/useCampaign'
import { ModuleTileGrid } from '../components/dashboard/ModuleTileGrid'

interface CoreToolCardProps {
  icon: React.ElementType
  label: string
  description: string
  to: string
}

function CoreToolCard({ icon, label, description, to }: CoreToolCardProps) {
  return (
    <LinkBox
      bg="gray.50"
      p={4}
      borderRadius="md"
      _hover={{ bg: 'gray.100', shadow: 'sm' }}
      transition="all 0.2s"
      cursor="pointer"
      asChild
    ><Box>
        <HStack gap={3}>
          <Icon as={icon} boxSize={5} color="accent" />
          <Box>
            <LinkOverlay asChild><RouterLink to={to}>
                <Text fontWeight="medium" fontSize="sm">{label}</Text>
              </RouterLink></LinkOverlay>
            <Text fontSize="xs" color="gray.500">{description}</Text>
          </Box>
        </HStack>
      </Box></LinkBox>
  );
}

export function DashboardPage() {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const { data: activeCampaigns, isLoading: campaignsLoading } = useCampaigns({ status: 'Active' })

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={1}>Dashboard</Heading>
          <Text color="gray.600" fontSize="sm">Organizer overview</Text>
        </Box>

        {/* Section 1: Modules */}
        <Box>
          <Heading size="md" mb={4}>Modules</Heading>
          <ModuleTileGrid />
        </Box>

        {/* Section 2: Active Campaigns */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <HStack justify="space-between" mb={4}>
            <Heading size="md">Active Campaigns</Heading>
            <Button size="sm" variant="ghost" colorPalette="brand" asChild><RouterLink to="/dashboard/campaigns">View All
                                          <FiArrowRight /></RouterLink></Button>
          </HStack>

          {campaignsLoading ? (
            <VStack py={6}>
              <Spinner size="md" color="accent" />
            </VStack>
          ) : !activeCampaigns?.length ? (
            <Box py={4} textAlign="center">
              <Text color="gray.500" fontSize="sm">No active campaigns</Text>
              <Button size="sm" variant='link' colorPalette="brand" mt={2} asChild><RouterLink to="/dashboard/campaigns">Go to Campaigns
                              </RouterLink></Button>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
              {activeCampaigns.map((campaign) => {
                const completion = campaign.totalContactCount > 0
                  ? Math.round((campaign.visitedContactCount / campaign.totalContactCount) * 100)
                  : 0
                return (
                  <Box
                    key={campaign.id}
                    p={4}
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.200"
                    _hover={{ shadow: 'md', borderColor: 'brand.200' }}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
                  >
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="semibold" fontSize="sm" truncate>
                        {campaign.label}
                      </Text>
                      <Badge colorPalette="green" fontSize="xs">Active</Badge>
                    </HStack>
                    <HStack gap={4} fontSize="xs" color="gray.500">
                      <Text>{campaign.totalContactCount} contacts</Text>
                      <Text>{campaign.totalTurfCount} turfs</Text>
                      <Text>{completion}% complete</Text>
                    </HStack>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Box>

        {/* Section 3: Contacts */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="md" mb={4}>Contacts</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={3}>
            <CoreToolCard
              icon={FiSearch}
              label="Contact Search"
              description="Look up contacts by name, address, or other criteria"
              to="/dashboard/contacts/search"
            />
            <CoreToolCard
              icon={FiList}
              label="Create lists"
              description="Create and manage contact lists for outreach"
              to="/dashboard/lists"
            />
            <CoreToolCard
              icon={FiUserPlus}
              label="Add contact"
              description="Add a new contact"
              to="/dashboard/contacts/new"
            />
          </SimpleGrid>
        </Box>

        {/* Section 4: Administration (admin only) */}
        {isAdmin && (
          <Box bg="surface" p={6} borderRadius="lg" shadow="md">
            <Heading size="md" mb={4}>Administration</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              <CoreToolCard
                icon={FiUsers}
                label="User Management"
                description="Manage user accounts, roles, and assignments"
                to="/dashboard/admin/users"
              />
              <CoreToolCard
                icon={FiMail}
                label="Invites"
                description="Issue and manage single-use account invites"
                to="/dashboard/admin/invites"
              />
              <CoreToolCard
                icon={FiGrid}
                label="Committees"
                description="Create and manage committees"
                to="/dashboard/admin/committees"
              />
            </SimpleGrid>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
